/* eslint-disable no-console */
import { Feature, Polygon } from "geojson";
import mask from "@turf/mask";
import booleanOverlap from "@turf/boolean-overlap";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import booleanWithin from "@turf/boolean-within";
import booleanCrosses from "@turf/boolean-crosses";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { Status } from "@iventis/domain-model/model/status";
import { createStaticStyleValue } from "@iventis/layer-style-helpers";
import { BehaviorSubject } from "rxjs";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import { DomainLayer } from "../../state/map.state.types";
import { LayerStorageScope, MapState } from "../../types/store-schema";
import { ViewByStatus } from "../../types/layer-view-by";
import { defaultAreaStyle } from "../../utilities/default-style-values";
import { Layer } from "./layer";

export abstract class MaskLayer extends Layer<Polygon[], AreaStyle> {
    protected source: Feature<Polygon>;

    protected readonly layerId = "mask-layer";

    protected readonly sourceId = this.layerId;

    protected readonly maskLayer: DomainLayer = {
        id: this.layerId,
        status: Status.Active,
        mapId: "",
        visible: true,
        locked: false,
        imported: false,
        name: "mask layer",
        styleType: StyleType.Area,
        areaStyle: {
            ...defaultAreaStyle,
            opacity: createStaticStyleValue(0.8),
            colour: createStaticStyleValue("#ffffff"),
        },
        lineStyle: undefined,
        pointStyle: undefined,
        iconStyle: undefined,
        modelStyle: undefined,
        lineModelStyle: undefined,
        areaModelStyle: undefined,
        previewIconUrl: "",
        dataFields: [],
        tooltipDataFieldIds: [],
        createdByUserId: "",
        createdByUserName: "",
        createdAt: undefined,
        lastUpdatedByUserId: "",
        lastUpdatedByUserName: "",
        lastUpdatedAt: undefined,
        viewBy: ViewByStatus.OBJECT,
        source: this.layerId,
        stamp: this.sourceId,
        selected: false,
        storageScope: LayerStorageScope.LocalOnly,
        remote: false,
    };

    constructor(polygons: Polygon[], state$: BehaviorSubject<MapState>) {
        super(state$);
        if (polygons?.length > 0) {
            const features: Feature<Polygon>[] = polygons.map((polygon) => ({ type: "Feature", geometry: polygon, properties: {} }));
            this.source = mask({ type: "FeatureCollection", features });
        }
    }

    protected abstract onMouseEvents(): void;

    public updateLocalSource(updatedPolygon: Polygon[]): void {
        const features: Feature<Polygon>[] = updatedPolygon.map((polygon) => ({ type: "Feature", geometry: polygon, properties: {} }));
        this.source = mask({ type: "FeatureCollection", features });
    }

    public areObjectsOverlappingMaskLayer(objects: Feature[]) {
        return objects.some((object) => {
            switch (object.geometry.type) {
                case "Point":
                    return booleanPointInPolygon(object.geometry.coordinates, this.source);
                case "LineString":
                    // If it is a single point on a line
                    if (object.geometry.coordinates.length === 1) {
                        return booleanPointInPolygon(object.geometry.coordinates[0], this.source);
                    }
                    return booleanCrosses(object, this.source) || booleanWithin(object, this.source);
                case "Polygon":
                    return booleanOverlap(object, this.source) || booleanWithin(object, this.source);
                default:
                    console.warn(`Unsupported geometry type ${object.geometry.type}`);
                    return true;
            }
        });
    }
}
